import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';
import './CVForm.css';

const CVForm: React.FC = () => {
  const { language } = useLanguage();
  
  // Debug: Log environment variables on component mount (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Environment Variables Check:', {
        SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'NOT FOUND',
        TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'NOT FOUND',
        PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'NOT FOUND',
        NODE_ENV: process.env.NODE_ENV
      });
    }
  }, []);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    privacyAccepted: false,
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB (Supabase storage limit)

      if (file.size > maxSize) {
        alert(
          language === 'nl'
            ? `Bestand is te groot (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum grootte is 10 MB. Comprimeer uw CV of gebruik een kleinere versie.`
            : `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 10 MB. Please compress your CV or use a smaller version.`
        );
        e.target.value = ''; // Clear the input
        setCvFile(null);
        return;
      }

      setCvFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB (Supabase storage limit)

      if (file.size > maxSize) {
        alert(
          language === 'nl'
            ? `Bestand is te groot (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum grootte is 10 MB. Comprimeer uw CV of gebruik een kleinere versie.`
            : `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 10 MB. Please compress your CV or use a smaller version.`
        );
        setCvFile(null);
        return;
      }

      setCvFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const uploadCV = async (file: File): Promise<string> => {
    if (!supabase) throw new Error('Supabase not configured');

    const fileExt = file.name.split('.').pop() || 'pdf';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `cv-submissions/${fileName}`;

    const { error } = await supabase.storage
      .from('applications')
      .upload(filePath, file, {
        cacheControl: '3600', // Cache for 1 hour to reduce egress
        upsert: false
      });

    if (error) throw error;

    // Get public URL (bucket is public)
    const { data: { publicUrl } } = supabase.storage
      .from('applications')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate required fields including privacy checkbox
    if (!formData.privacyAccepted) {
      alert(language === 'nl' 
        ? 'U moet akkoord gaan met het privacybeleid om het formulier te kunnen verzenden.'
        : 'You must agree to the privacy policy to submit the form.');
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Upload CV to Supabase storage first (no file size limits!)
      let cvUrl = '';
      if (cvFile) {
        cvUrl = await uploadCV(cvFile);
      }

      // EmailJS Configuration
      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Validate that all required values are present
      if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
        throw new Error(
          language === 'nl'
            ? 'EmailJS configuratie ontbreekt. Controleer je .env bestand of neem contact op met de beheerder.'
            : 'EmailJS configuration is missing. Check your .env file or contact the administrator.'
        );
      }

      // Send email with CV link instead of attachment
      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          full_name: formData.fullName,
          from_name: formData.fullName,
          from_email: formData.email,
          phone: formData.phone,
          message: formData.message || (language === 'nl' ? '(Geen bericht)' : '(No message)'),
          cv_url: cvUrl || (language === 'nl' ? 'Geen CV geüpload' : 'No CV uploaded'),
        },
        PUBLIC_KEY
      );

      if (response.status === 200) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          message: '',
          privacyAccepted: false,
        });
        setCvFile(null);
      } else {
        // Log error for debugging
        if (process.env.NODE_ENV === 'development') {
          console.error('Form submission error:', response);
        }
        setSubmitStatus('error');
      }
    } catch (error: any) {
      // Log error for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
      
      // Provide user-friendly error messages
      let errorMessage = language === 'nl' 
        ? 'Er is iets misgegaan. Probeer het opnieuw of neem contact met ons op.'
        : 'Something went wrong. Please try again or contact us directly.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.status === 413 || error?.response?.status === 413) {
        errorMessage = language === 'nl'
          ? 'Het CV bestand is te groot. Maximum grootte is 10 MB. Comprimeer het bestand en probeer opnieuw.'
          : 'CV file is too large. Maximum size is 10 MB. Please compress the file and try again.';
      } else if (error?.text) {
        // EmailJS specific errors
        try {
          const errorData = JSON.parse(error.text);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Ignore JSON parse errors
        }
      }
      
      // Show error to user
      alert(errorMessage);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cv-form-page">
      {/* Hero Section */}
      <div className="cv-form-hero">
        <div className="cv-form-hero-content">
          <nav className="cv-form-breadcrumb">
            <a href="/">{language === 'nl' ? 'Home' : 'Home'}</a>
            <span className="breadcrumb-separator">›</span>
            <span>{language === 'nl' ? 'Stuur je CV' : 'Send Your CV'}</span>
          </nav>
          <h1 className="cv-form-hero-title">
            {language === 'nl' ? 'Stuur je CV' : 'Send Your CV'}
          </h1>
          <p className="cv-form-hero-subtitle">
            {language === 'nl' 
              ? 'Neem de eerste stap naar je nieuwe carrière. Upload je CV en wij nemen contact met je op.'
              : 'Take the first step towards your new career. Upload your CV and we\'ll get in touch.'}
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="cv-form-container">
        <div className="cv-form-wrapper">
          <div className="cv-form-info">
            <div className="cv-form-info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
              </div>
              <h3>{language === 'nl' ? 'Bel ons' : 'Call us'}</h3>
              <p>020 308 6271</p>
            </div>
            
            <div className="cv-form-info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h3>{language === 'nl' ? 'E-mail ons' : 'Email us'}</h3>
              <p>info@meijerknijnenberg.com</p>
            </div>

            <div className="cv-form-info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <h3>{language === 'nl' ? 'Bezoek ons' : 'Visit us'}</h3>
              <p>Nieuwezijds voorburgwal 334-2<br/>Amsterdam</p>
            </div>

            <div className="cv-form-trust">
              <div className="trust-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9,12 12,15 16,10"/>
                </svg>
                <span>{language === 'nl' ? '100% Vertrouwelijk' : '100% Confidential'}</span>
              </div>
              <div className="trust-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>{language === 'nl' ? 'Reactie binnen 24 uur' : 'Response within 24 hours'}</span>
              </div>
            </div>
          </div>

          <form className="cv-form" onSubmit={handleSubmit}>
            {submitStatus === 'success' ? (
              <div className="form-success">
                <div className="success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="9,12 12,15 16,10"/>
                  </svg>
                </div>
                <h2>{language === 'nl' ? 'Bedankt voor je inzending!' : 'Thank you for your submission!'}</h2>
                <p>{language === 'nl' 
                  ? 'We hebben je CV ontvangen en nemen zo snel mogelijk contact met je op.'
                  : 'We have received your CV and will contact you as soon as possible.'}</p>
                <button 
                  type="button" 
                  className="submit-btn"
                  onClick={() => setSubmitStatus('idle')}
                >
                  {language === 'nl' ? 'Nog een CV versturen' : 'Send another CV'}
                </button>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fullName">
                      {language === 'nl' ? 'Volledige naam' : 'Full Name'} *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder={language === 'nl' ? 'Jouw naam' : 'Your name'}
                      required
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label htmlFor="email">
                      {language === 'nl' ? 'E-mailadres' : 'Email Address'} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={language === 'nl' ? 'jouw@email.nl' : 'your@email.com'}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">
                      {language === 'nl' ? 'Telefoonnummer' : 'Phone Number'} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+31 6 12345678"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="cv">
                    {language === 'nl' ? 'Upload je CV' : 'Upload your CV'} *
                  </label>
                  <label 
                    className="file-upload-area"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input
                      type="file"
                      id="cv"
                      name="cv"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required={!cvFile}
                    />
                    {cvFile ? (
                      <div className="file-selected">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10 9 9 9 8 9"/>
                        </svg>
                        <span>{cvFile.name}</span>
                        <button 
                          type="button" 
                          className="remove-file"
                          onClick={(e) => {
                            e.preventDefault();
                            setCvFile(null);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="file-upload-content">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <p>
                          {language === 'nl'
                            ? 'Klik of sleep je CV hierheen'
                            : 'Click or drag your CV here'}
                        </p>
                        <span>
                          {language === 'nl'
                            ? 'PDF, DOC, DOCX (max 10 MB)'
                            : 'PDF, DOC, DOCX (max 10 MB)'}
                        </span>
                      </div>
                    )}
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    {language === 'nl' ? 'Bericht (optioneel)' : 'Message (optional)'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={language === 'nl' 
                      ? 'Vertel ons iets over jezelf en je carrièredoelen...'
                      : 'Tell us something about yourself and your career goals...'}
                    rows={4}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      id="privacyAccepted"
                      name="privacyAccepted"
                      checked={formData.privacyAccepted}
                      onChange={handleInputChange}
                      required
                      className="checkbox-input"
                    />
                    <label htmlFor="privacyAccepted" className="checkbox-label-new">
                      <span className="checkbox-custom-new"></span>
                      <span className="checkbox-text-new">
                        {language === 'nl' 
                          ? 'Ik ga akkoord met het privacybeleid en geef toestemming voor het verwerken van mijn gegevens.'
                          : 'I agree to the privacy policy and consent to the processing of my data.'}
                      </span>
                    </label>
                  </div>
                </div>

                {submitStatus === 'error' && (
                  <div className="form-error">
                    {language === 'nl' 
                      ? 'Er is iets misgegaan. Probeer het opnieuw of neem contact met ons op.'
                      : 'Something went wrong. Please try again or contact us directly.'}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting || !formData.privacyAccepted}
                >
                  {isSubmitting ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      {language === 'nl' ? 'Verstuur CV' : 'Send CV'}
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CVForm;

